import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Image as ImageIcon, RotateCcw, Save, AlertTriangle } from 'lucide-react';
import { db } from '@/lib/firebase';
import { IMG, IMAGE_KEYS, type ImageKey } from '@/data/images';

const GROUP_ORDER = [...new Set(IMAGE_KEYS.map((k) => k.group))];

export function Images() {
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [broken, setBroken] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    getDoc(doc(db, 'settings', 'images'))
      .then((snap) => { if (snap.exists()) setOverrides({ ...(snap.data() as Record<string, string>) }); })
      .catch(() => { /* no doc yet — start blank (defaults apply) */ });
  }, []);

  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 3500); }

  function setUrl(key: ImageKey, v: string) {
    setOverrides((o) => ({ ...o, [key]: v }));
    setBroken((b) => { if (!b.has(key)) return b; const n = new Set(b); n.delete(key); return n; });
  }
  function resetKey(key: ImageKey) {
    setOverrides((o) => { const n = { ...o }; delete n[key]; return n; });
    setBroken((b) => { if (!b.has(key)) return b; const n = new Set(b); n.delete(key); return n; });
  }

  /** The URL actually shown on the site: a non-blank override, else the default. */
  function effectiveUrl(key: ImageKey): string {
    const v = overrides[key];
    return v && v.trim() !== '' ? v.trim() : IMG[key];
  }
  const isOverridden = (key: ImageKey) => {
    const v = overrides[key];
    return typeof v === 'string' && v.trim() !== '' && v.trim() !== IMG[key];
  };

  async function save() {
    setBusy(true);
    try {
      // Write only the non-blank overrides. We overwrite the whole doc (not a
      // partial merge) so a "reset to default" row actually drops out of the
      // stored overrides — the provider then falls back to the default URL.
      const payload: Record<string, string> = {};
      for (const { key } of IMAGE_KEYS) {
        const v = overrides[key];
        if (typeof v === 'string' && v.trim() !== '') payload[key] = v.trim();
      }
      payload.updatedAt = new Date().toISOString();
      await setDoc(doc(db, 'settings', 'images'), payload);
      flash('Images saved — changes are live on the site.');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-espresso">Images</h1>
          <p className="mt-1 font-body text-sm text-muted">Swap any picture on the public site by pasting a new URL. No redeploy needed.</p>
        </div>
        <button onClick={save} disabled={busy} className="btn-primary !px-5 !py-2.5">
          <Save size={16} /> {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="mt-5 flex items-start gap-2.5 border border-line bg-paper p-4">
        <ImageIcon size={16} className="mt-0.5 shrink-0 text-gold-deep" />
        <p className="font-body text-sm leading-relaxed text-muted">
          Upload your image to a public host (e.g. an image-hosting app), copy the <strong className="text-espresso">direct image URL</strong>,
          and paste it here. Leave a field blank to keep the default. The preview updates as you type.
        </p>
      </div>

      {GROUP_ORDER.map((group) => (
        <section key={group} className="mt-8">
          <p className="eyebrow mb-3">{group}</p>
          <div className="divide-y divide-line border border-line bg-paper">
            {IMAGE_KEYS.filter((k) => k.group === group).map(({ key, label }) => (
              <div key={key} className="grid gap-4 p-5 sm:grid-cols-[128px_1fr]">
                {/* Preview */}
                <div className="relative aspect-[4/3] w-full overflow-hidden border border-line bg-cream">
                  {broken.has(key) ? (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-center text-blush-deep">
                      <AlertTriangle size={18} />
                      <span className="px-2 font-body text-[10px] leading-tight">Couldn't load</span>
                    </div>
                  ) : (
                    <img
                      src={effectiveUrl(key)}
                      alt={label}
                      className="h-full w-full object-cover"
                      onError={() => setBroken((b) => new Set(b).add(key))}
                    />
                  )}
                </div>

                {/* Controls */}
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <label className="label !mb-0" htmlFor={`img-${key}`}>{label}</label>
                    {isOverridden(key) ? (
                      <span className="chip border-gold bg-gold/15 text-[10px] text-espresso">Custom</span>
                    ) : (
                      <span className="chip border-line bg-white text-[10px] text-muted">Default</span>
                    )}
                  </div>
                  <input
                    id={`img-${key}`}
                    className="field mt-2"
                    type="url"
                    inputMode="url"
                    placeholder="https://…/your-image.jpg  (leave blank for default)"
                    value={overrides[key] ?? ''}
                    onChange={(e) => setUrl(key, e.target.value)}
                  />
                  <button
                    onClick={() => resetKey(key)}
                    disabled={!isOverridden(key)}
                    className="mt-2 inline-flex items-center gap-1.5 font-body text-xs text-gold-deep hover:underline disabled:cursor-not-allowed disabled:text-muted/40 disabled:no-underline"
                  >
                    <RotateCcw size={13} /> Reset to default
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>
      )}
    </div>
  );
}
